import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Save, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminExchangeRates = ({ token }) => {
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/exchange-rates/`);
      setRates(res.data);
    } catch { toast.error('Failed to load rates'); }
    setLoading(false);
  };

  useEffect(() => { fetchRates(); }, []);

  const handleSave = async (currency) => {
    const newRate = parseFloat(editValue);
    if (isNaN(newRate) || newRate <= 0) { toast.error('Invalid rate'); return; }
    try {
      await axios.put(`${API}/exchange-rates/${currency}`, { rate: newRate }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${currency} rate updated`);
      setEditing(null);
      fetchRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Exchange Rates (kWh → Local Currency)</h2>
        <Button onClick={fetchRates} variant="outline" size="sm" data-testid="refresh-rates-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
      <p className="text-sm text-gray-500">
        Rate = local currency amount per 1 kWh. Example: UAH 4.32 means 1 kWh = 4.32 UAH.
      </p>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Symbol</TableHead>
                <TableHead>Rate (per kWh)</TableHead>
                <TableHead>1000 kWh =</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.map(rate => (
                <TableRow key={rate.currency}>
                  <TableCell className="font-bold">{rate.currency}</TableCell>
                  <TableCell>{rate.country}</TableCell>
                  <TableCell>{rate.symbol}</TableCell>
                  <TableCell>
                    {editing === rate.currency ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-28"
                        data-testid={`rate-input-${rate.currency}`}
                      />
                    ) : (
                      <span>{rate.rate}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-amber-600 font-medium">
                    {rate.symbol}{(1000 * rate.rate).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {editing === rate.currency ? (
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => handleSave(rate.currency)} data-testid={`save-rate-${rate.currency}`}>
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => { setEditing(rate.currency); setEditValue(rate.rate.toString()); }} data-testid={`edit-rate-${rate.currency}`}>
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loading && <div className="text-center py-8 text-gray-400">Loading...</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminExchangeRates;
